// Context serialization helpers for runtime compile prelude generation.
// Example: object map `{ color: #fff }` -> `{ color: #fff; }`.
import { serializeArrayVariable } from '@lentystyle/compiler/variables/array-variable';
import type { LuisArrayValue, LuisCompileContext, LuisObjectValue } from "./context";

// Convert context variables into synthetic prelude declarations.
export function buildContextPrelude(context: LuisCompileContext): string {
  const classLines = Object.entries(context.classVariables).map(([name, value]) => {
    return `${name}: ${value};`;
  });

  const objectLines = Object.entries(context.objectVariables).map(([name, value]) => {
    return `${name}: ${serializeObjectValue(value)};`;
  });

  const arrayLines = Object.entries(context.arrayVariables).map(([name, value]) => {
    return `${name}: ${serializeArrayVariable(value)};`;
  });

  // Return joined synthetic declarations for pre-compilation context.
  return [...classLines, ...objectLines, ...arrayLines].join("\n");
}

// Serialize object variable maps back into `{ key: value; }` format.
export function serializeObjectValue(value: LuisObjectValue): string {
  const entries = Object.entries(value).map(([key, val]) => `${key}: ${val};`);
  // Return object value as compact declaration block text.
  return `{ ${entries.join(" ")} }`;
}

// Deep-clone object-variable map to avoid accidental shared mutations.
export function cloneObjectVariableMap(value: Record<string, LuisObjectValue>): Record<string, LuisObjectValue> {
  const next: Record<string, LuisObjectValue> = {};
  // Copy each object variable entry and its key/value pairs.
  for (const [name, objectValue] of Object.entries(value)) {
    next[name] = { ...objectValue };
  }

  // Return cloned object map.
  return next;
}

// Deep-clone array-variable map to avoid accidental shared mutations.
export function cloneArrayVariableMap(value: Record<string, LuisArrayValue>): Record<string, LuisArrayValue> {
  const next: Record<string, LuisArrayValue> = {};
  // Copy each array variable entry and clone its item array.
  for (const [name, arrayValue] of Object.entries(value)) {
    next[name] = [...arrayValue];
  }

  // Return cloned array map.
  return next;
}
